import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { Panel, btn, field } from "@/components/ui-kit";
import { FixButton } from "@/components/sap/FixDialog";

const L = ({ children }) => <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">{children}</span>;
const sel = `h-9 w-full rounded-md border px-2 text-sm ${field}`;
const blank = () => ({ partner: "", po_number: "", po_date: "", delivery_date: "", depot: "", notes: "", lines: [{ item: "", qty: "", price: "" }] });

const Line = ({ ln, i, items, set, remove, code, priced }) => (
  <div className="grid gap-2 sm:grid-cols-[1fr_120px_120px_40px]">
    <Input list={`noedi-items-${code}`} placeholder="Item: part no., SAP or customer code - type a new code to add it" value={ln.item} onChange={(e) => set({ ...ln, item: e.target.value })} className={field} data-testid={`noedi-line-${i}-item`} />
    {i === 0 && <datalist id={`noedi-items-${code}`}>{items.map((it) => <option key={it.part_no} value={it.part_no}>{`SAP ${it.ep_sku || "?"}${(it.customer_items || {})[code] ? ` · cust ${it.customer_items[code]}` : ""} · ${it.description}`}</option>)}</datalist>}
    <Input type="number" min="0" placeholder={priced ? "Pallets" : "Qty"} value={ln.qty} onChange={(e) => set({ ...ln, qty: e.target.value })} className={field} data-testid={`noedi-line-${i}-qty`} />
    <Input type="number" min="0" step="0.01" placeholder={priced ? "Price (auto)" : "Price (optional)"} value={ln.price} onChange={(e) => set({ ...ln, price: e.target.value })} className={field} data-testid={`noedi-line-${i}-price`} />
    <Button size="sm" variant="ghost" onClick={remove} className={btn.danger} data-testid={`noedi-line-${i}-remove`}><Trash2 className="h-3.5 w-3.5" /></Button>
  </div>
);

export const NonEdiForm = ({ partners, items, reload }) => {
  const [o, setO] = useState(blank());
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [issues, setIssues] = useState(null);
  const lineBody = () => o.lines.filter((x) => x.item || x.qty).map((x) => ({ item: x.item.trim(), qty: Number(x.qty) || 0, price: x.price === "" ? null : Number(x.price) }));
  const ob = () => ({ ...o, depot: o.depot.trim(), lines: lineBody() });
  const check = () => api.post("/manual-orders/price", { ...o, lines: lineBody() }).then((r) => setQuote(r.data)).catch((e) => toast.error(errMsg(e)));
  const p = partners.find((x) => x.code === o.partner);
  const priced = !!p?.pricing?.breaks?.length;
  const dps = p?.idoc?.delivery_points || [];
  const setLine = (i, v) => setO({ ...o, lines: o.lines.map((x, j) => (j === i ? v : x)) });
  const checkIssues = async () => {
    try {
      const { data } = await api.post("/manual-orders/check", ob());
      setIssues(data.errors.length ? data : null);
      return data.errors.length === 0;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const afterFix = () => { reload?.(); checkIssues(); };
  const save = async () => {
    setBusy(true);
    try {
      if (!(await checkIssues())) return;
      const body = ob();
      const { data } = await api.post("/manual-orders", body);
      setDone(data);
      setIssues(null);
      data.error ? toast.warning(`Order ${data.po_number} saved but SAP IDoc not created: ${data.error}`) : toast.success(`Order ${data.po_number} saved - SAP IDoc ${data.idoc_file} created`);
      setO({ ...blank(), partner: o.partner });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel title="Enter an order (customer without EDI)" testId="noedi-form">
      <div className="space-y-4 p-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <label className="block"><L>Customer</L>
            <select value={o.partner} onChange={(e) => setO({ ...o, partner: e.target.value, depot: "" })} className={sel} data-testid="noedi-partner">
              <option value="">Choose customer…</option>
              {partners.map((x) => <option key={x.code} value={x.code}>{x.name} (.{x.idoc.ext})</option>)}
            </select>
          </label>
          <label className="block"><L>Delivery point (GLN / depot - type a new one to add it)</L>
            <Input list="noedi-depots" value={o.depot} onChange={(e) => setO({ ...o, depot: e.target.value })} placeholder={p ? "Choose or type GLN / depot code" : "Choose customer first"} className={field} data-testid="noedi-depot" disabled={!p} />
            <datalist id="noedi-depots">{dps.map((d) => <option key={d.gln || d.depot_code} value={d.gln || d.depot_code}>{`${d.name || d.gln} · ship-to ${d.sap_ship_to || "not set"}`}</option>)}</datalist>
          </label>
          <label className="block"><L>PO number</L><Input value={o.po_number} onChange={(e) => setO({ ...o, po_number: e.target.value })} className={field} data-testid="noedi-po" /></label>
          <label className="block"><L>PO date (DD/MM/YYYY)</L><Input placeholder="DD/MM/YYYY" value={o.po_date} onChange={(e) => setO({ ...o, po_date: e.target.value })} className={field} data-testid="noedi-po-date" /></label>
          <label className="block"><L>Delivery date (DD/MM/YYYY)</L><Input placeholder="DD/MM/YYYY" value={o.delivery_date} onChange={(e) => setO({ ...o, delivery_date: e.target.value })} className={field} data-testid="noedi-delivery-date" /></label>
        </div>
        <label className="block"><L>Notes (go in the IDoc header text if set to "Order notes")</L><Input value={o.notes} onChange={(e) => setO({ ...o, notes: e.target.value })} className={field} data-testid="noedi-notes" /></label>
        <div className="space-y-2"><L>Lines</L>
          {o.lines.map((ln, i) => <Line key={i} ln={ln} i={i} items={p?.pricing?.breaks?.length ? items.filter((it) => (it.customer_items || {})[o.partner] || (p.pricing.prices || {})[it.ep_sku]) : items} code={o.partner} priced={priced} set={(v) => { setQuote(null); setLine(i, v); }} remove={() => setO({ ...o, lines: o.lines.filter((_, j) => j !== i) })} />)}
          {priced && <p className="text-xs text-slate-500">Quantities in pallets. Leave price blank to use the price break for the total pallets on the order.</p>}
          {quote && <div className="text-xs text-slate-300" data-testid="noedi-quote">{quote.price_break ? `Price break ${quote.price_break} - ` : ""}{quote.lines.map((x) => `${x.item}: £${x.price.toLocaleString("en-GB", { minimumFractionDigits: 2 })}`).join(", ")} - value £{quote.value.toLocaleString("en-GB", { minimumFractionDigits: 2 })}{quote.errors.length ? <span className="text-rose-300"> {quote.errors.join("; ")}</span> : null}</div>}
        </div>
        {issues && <div className="space-y-2 rounded-sm border border-rose-900/60 bg-rose-950/20 p-3" data-testid="noedi-issues">
          <ul className="list-disc pl-5 text-xs text-rose-300">{issues.errors.map((e) => <li key={e}>{e}</li>)}</ul>
          <FixButton fix={issues.fix && { ...issues.fix, source: `manual order ${o.po_number}` }} onDone={afterFix} small testId="noedi-form-fix" />
        </div>}
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" className={btn.ghost} onClick={() => setO({ ...o, lines: [...o.lines, { item: "", qty: "", price: "" }] })} data-testid="noedi-add-line"><Plus className="mr-1.5 h-4 w-4" /> Add line</Button>
          {priced && <Button variant="ghost" className={btn.ghost} onClick={check} data-testid="noedi-check-prices">Check prices</Button>}
          {done && <Link to={`/orders/${done.order_id}`} className="text-xs text-emerald-300 underline" data-testid="noedi-last-order">Open order {done.po_number}{done.idoc_file ? ` (${done.idoc_file})` : ""}</Link>}
          <Button onClick={save} disabled={busy} className={`ml-auto ${btn.primary}`} data-testid="noedi-save">{busy ? "Saving…" : "Save order and create SAP IDoc"}</Button>
        </div>
      </div>
    </Panel>
  );
};

export const useNonEdiData = () => {
  const [partners, setPartners] = useState([]);
  const [items, setItems] = useState([]);
  const reload = () => {
    api.get("/partners").then((r) => setPartners(r.data.partners.filter((x) => x.idoc?.enabled)));
    api.get("/items").then((r) => setItems((r.data.items || r.data).filter((x) => x.type !== "RM")));
  };
  useEffect(() => { reload(); }, []);
  return { partners, items, reload };
};
